import { DiagnosticSummary } from "./summariseDiagnostics";
import { formatDateTime } from "@/utilities/formatDateTime";
import { formatNumber } from "@/utilities/formatNumber";

/** A measurement as the grid shows it, already formatted and placed in its group. */
export interface MotorDataRow {
  id: string;
  diagnosticId: string;
  /** Group then leaf, which is what the tree grid reads. */
  groupPath: string[];
  source: string;
  latest: string;
  min: string;
  max: string;
  average: string;
  count: number;
  unit: string;
  lastUpdated: string;
}

/** What the row builder needs from the page: the wording and the language. */
export interface RowLabels {
  themeLabel: (theme: DiagnosticSummary["theme"]) => string;
  unknownSource: string;
  language: string;
  missing: string;
}

/**
 * Turns the summaries into grid rows, grouped by theme.
 *
 * Two diagnostics of a vehicle can carry the same name — the same reading off two sources, say —
 * and the tree grid keys its groups by the path, so identical paths would collapse two
 * measurements into one row. The second one onwards is numbered to keep them apart.
 *
 * @param summaries - The measurements, in display order.
 * @param labels - See {@link RowLabels}.
 */
export function toMotorDataRows(
  summaries: DiagnosticSummary[],
  labels: RowLabels,
): MotorDataRow[] {
  const seen = new Map<string, number>();

  return summaries.map(summary => {
    const group = labels.themeLabel(summary.theme);
    const key = `${group}::${summary.name}`;
    const count = (seen.get(key) ?? 0) + 1;
    seen.set(key, count);

    return {
      id: summary.diagnosticId,
      diagnosticId: summary.diagnosticId,
      groupPath: [group, count === 1 ? summary.name : `${summary.name} (${count})`],
      source: summary.sourceName ?? summary.sourceId ?? labels.unknownSource,
      latest: formatReading(summary.latest, labels),
      min: formatNumber(summary.min, labels.language, 2, labels.missing),
      max: formatNumber(summary.max, labels.language, 2, labels.missing),
      average: formatNumber(summary.average, labels.language, 2, labels.missing),
      count: summary.count,
      unit: summary.unit,
      lastUpdated: formatDateTime(summary.latestAt, labels.language, labels.missing),
    };
  });
}

/**
 * The latest reading as text.
 *
 * Not every diagnostic is a number: ignition and door states arrive as booleans, and a few
 * readings as text. Forcing them all through the number formatter would show a dash for each.
 */
function formatReading(value: unknown, labels: RowLabels): string {
  if (value == null) {
    return labels.missing;
  }
  if (typeof value === "number") {
    return formatNumber(value, labels.language, 2, labels.missing);
  }
  if (typeof value === "boolean") {
    return String(value);
  }
  if (typeof value === "string") {
    return value;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
